<template>
  <div class="booking-space-slot-picker" :class="{'is-loading': isLoading}">
    <div v-if="dateSlotData.length">
      <div v-if="lowestCost" class="booking-space-slot-picker__lowest-cost">Starting at ${{ lowestCost }} per slot</div>
      <div class="booking-space-slot-picker__lead-in">Select your date(s) and time slot(s)</div>

      <div class="booking-space-slot-picker__mode">
        <button type="button" @click="changeMode('single')" :class="{'is-active': mode === 'single' }">Single Date</button>
        <button type="button" @click="changeMode('range')" :class="{'is-active': mode === 'range' }">Date Range</button>
      </div>

      <v-date-picker
        v-model="selectedDates"
        :available-dates="availableDates"
        v-if="mode == 'single'"
      >
        <input
          id="date"
          slot-scope="{ inputProps, inputEvents, isDragging }"
          placeholder="Select Date"
          :readonly="true"
          class="date-input"
          v-bind="inputProps"
          v-on="inputEvents">
      </v-date-picker>

      <v-date-picker
        mode="range"
        v-model="selectedDates"
        :available-dates="availableDates"
        @drag="dragValue = $event"
        v-if="mode == 'range'"
      >
        <input
          id="date"
          slot-scope="{ inputProps, inputEvents, isDragging }"
          placeholder="Select Date Range"
          :readonly="true"
          class="date-input"
          v-bind="inputProps"
          v-on="inputEvents">
      </v-date-picker>

      <multiselect
        v-model="selectedSlots"
        :options="availableSlots"
        :multiple="true"
        group-values="slots"
        group-label="date_label"
        :group-select="true"
        :close-on-select="false"
        :searchable="false"
        placeholder=""
        track-by="id"
        label="label"
        selectLabel=""
        deselectLabel=""
        selectedLabel=""
        selectGroupLabel="Select All"
        deselectGroupLabel="Unselect All"
        ref="multiselect"
      >
        <template slot="noOptions">
          Please select a date
        </template>

        <template slot="tag">
          <span>Available Time Slots</span>
        </template>

        <template slot="selection" slot-scope="{ values, search, isOpen }">
          <span>Available Time Slots</span>
        </template>

        <template slot="beforeList" v-if="availableSlots.length">
          <li @click="closeAvailableSlots" class="multiselect__close">Close</li>
        </template>
      </multiselect>

      <ul v-if="sortedSelectedSlots.length" class="selected-slots">
        <li v-for="slot in sortedSelectedSlots" class="selected-slot">
          <div class="selected-slot__info">
            <strong>{{ slot.date }}</strong>
            {{ slot.label }}

            <button type="button" @click="removeSlot(slot)" aria-label="Remove Slot">&times;</button>
          </div>
        </li>
      </ul>

      <div v-if="selectedSlots.length" class="booking-space-slot-picker__actions">
        <div class="booking-space-slot-picker__total">Total: ${{ totalCost.toFixed(2) }}</div>
        <button type="button" class="btn btn--fill booking-space-slot-picker__book" @click="handleBook">Book Now</button>
      </div>
    </div>

    <div v-if="!dateSlotData.length && !isLoading">
      There are currently no available slots.
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { format, eachDayOfInterval } from 'date-fns'
import 'vue-multiselect/dist/vue-multiselect.min.css'

export default {
  name: 'BookingSpaceSlotPicker',

  components: {
    Multiselect
  },

  data() {
    return {
      selectedDates: null,
      dateSlotData: [],
      dragValue: null,
      selectedSlots: [],
      availableSlots: [],
      slotData: null,
      isLoading: true,
      mode: 'single'
    }
  },

  computed: {
    availableDates: function () {
      if (this.dragValue) {
        const dates = this.dateSlotData.map(d => d.date_str).sort((a, b) => new Date(a) - new Date(b))

        return { start: this.dragValue.start, end: dates[dates.length - 1]}
      } else {
        return this.dateSlotData.map(d => {
          return { start: d.date_str, end: d.date_str }
        })
      }
    },

    lowestCost: function () {
      if (!this.slotData) {
        return false
      }

      const costs = Object.values(this.slotData).map(slot => slot.cost)
      return Math.min(...costs)
    },

    sortedSelectedSlots: function () {
      return this.selectedSlots.sort((a, b) => a.sort - b.sort)
    },

    totalCost: function () {
      return this.selectedSlots.reduce((total, slot) => total += slot.cost, 0)
    }
  },

  watch: {
    selectedDates: function (dates) {
      if (this.mode === 'single' && dates) {
        this.availableSlots = this.dateSlotData.filter(data => data.date_str === format(dates, 'y-MM-dd'))
      }

      if (this.mode === 'range' && dates) {
        const dateStrs = eachDayOfInterval({ start: dates.start, end: dates.end }).map(date => format(date, 'y-MM-dd'))
        this.availableSlots = this.dateSlotData.filter(data => dateStrs.includes(data.date_str))
      }
    }
  },

  methods: {
    removeSlot: function (slot) {
      const index = this.selectedSlots.findIndex(s => s.id === slot.id)
      this.selectedSlots = [
        ...this.selectedSlots.slice(0, index),
        ...this.selectedSlots.slice(index + 1)
      ]
    },

    handleBook: function () {
      const slotIds = this.selectedSlots.map(s => s.id)

      window.location.href = `${window._slotPicker.bookingRequestUrl}?slot=${slotIds.join(',')}`
    },

    changeMode: function (mode) {
      this.mode = mode
      this.selectedDates = null
    },

    closeAvailableSlots: function () {
      this.$refs.multiselect.deactivate()
    }
  },

  created() {
    const url = `${window._slotPicker.controllerUrl}slotdata/${window._slotPicker.bookingSpaceId}`

    $.getJSON(url).then(resp => {
      this.dateSlotData = resp
      this.isLoading = false

      this.slotData = resp.reduce((obj, date) => {
        const s = date.slots.reduce((slotObj, slot) => Object.assign(slotObj, { [slot.id]: slot }), {})
        return Object.assign(obj, s)
      }, {})
    })
  }
}
</script>
